import {useMutation} from '@apollo/client'
import {GoogleOAuthProvider, useGoogleLogin} from '@react-oauth/google'
import {useEffect, useState} from 'react'
import {Badge, Button, Card, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {defineMessages, useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {CREATE_INTEGRATION} from '../../gql/mutations/integrationsMutations'
import {IResCreateIntegration} from '../../types/integrationsTypes'
import {
  RING_CENTRAL_APP_SERVER,
  RING_CENTRAL_CLIENT_ID,
  GOOGLE_CLIENT_ID,
  STORAGE_NAMES,
  FEATURE_SHOW_GOOGLE_INTEGRATION,
} from '../../helpers/consts'
import {ModalRingCentralConnect} from '../../pages/integrations/components/ModalRingCentralConnect'
import {useTelephony} from '../../hooks/useTelephony'

const M = defineMessages({
  // Toasts
  integrationError: {
    id: 'shell.integrations.toast.error',
    defaultMessage: 'Error performing integration',
  },
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
  googleConnected: {
    id: 'shell.integrations.toast.googleConnected',
    defaultMessage: 'Google connected successfully',
  },
  googleDisconnected: {
    id: 'shell.integrations.toast.googleDisconnected',
    defaultMessage: 'Google disconnected',
  },
  campaignSaved: {
    id: 'shell.integrations.toast.campaignSaved',
    defaultMessage: 'PolicyDial campaign {campaign} saved for this session.',
  },
  campaignDefault: {
    id: 'shell.integrations.toast.campaignDefault',
    defaultMessage: 'PolicyDial will use the organization default campaign {campaign}.',
  },
  campaignCleared: {
    id: 'shell.integrations.toast.campaignCleared',
    defaultMessage: 'PolicyDial session campaign cleared.',
  },
  reconnectHint: {
    id: 'shell.integrations.toast.reconnectHint',
    defaultMessage: 'Reconnect PolicyDial to apply the new campaign.',
  },

  // Connection status
  registering: {id: 'shell.integrations.status.registering', defaultMessage: 'Registering…'},
  connected: {id: 'shell.integrations.status.connected', defaultMessage: 'Connected'},
  waiting: {id: 'shell.integrations.status.waiting', defaultMessage: 'Waiting…'},
  disconnected: {id: 'shell.integrations.status.disconnected', defaultMessage: 'Disconnected'},
  notConnected: {id: 'shell.integrations.status.notConnected', defaultMessage: 'Not connected'},
  notConfigured: {id: 'shell.integrations.status.notConfigured', defaultMessage: 'Not configured'},
  active: {id: 'common.status.active', defaultMessage: 'Active'},

  // Links
  openIntegrations: {
    id: 'shell.integrations.link.openIntegrations',
    defaultMessage: 'Open integrations page',
  },
  openTemplates: {
    id: 'shell.integrations.link.openTemplates',
    defaultMessage: 'Open templates page',
  },
  templates: {id: 'MENU.item.templates', defaultMessage: 'Templates'},

  // PolicyDial details modal
  detailsTitle: {
    id: 'shell.integrations.details.title',
    defaultMessage: 'PolicyDial Connection Details',
  },
  sessionCampaign: {
    id: 'shell.integrations.details.sessionCampaign',
    defaultMessage: 'Campaign for this session',
  },
  useOrgDefault: {
    id: 'shell.integrations.details.useOrgDefault',
    defaultMessage: 'Use organization default',
  },
  loadingCampaigns: {id: 'shell.integrations.details.loading', defaultMessage: 'Loading…'},
  loadCampaigns: {
    id: 'shell.integrations.details.loadCampaigns',
    defaultMessage: 'Load campaigns',
  },
  fieldAgent: {id: 'shell.integrations.details.agent', defaultMessage: 'Agent:'},
  fieldPhone: {id: 'shell.integrations.details.phone', defaultMessage: 'Phone:'},
  fieldStatus: {id: 'shell.integrations.details.status', defaultMessage: 'Status:'},
  fieldActiveCampaign: {
    id: 'shell.integrations.details.activeCampaign',
    defaultMessage: 'Active campaign:',
  },
  fieldPauseCode: {id: 'shell.integrations.details.pauseCode', defaultMessage: 'Pause code:'},
  fieldSession: {id: 'shell.integrations.details.session', defaultMessage: 'Session:'},

  // Inbound groups modal
  inboundTitle: {
    id: 'shell.integrations.inbound.title',
    defaultMessage: 'Closer inbound group selection',
  },
  inboundHint: {
    id: 'shell.integrations.inbound.hint',
    defaultMessage: 'Select the inbound groups that should receive calls for this PolicyDial session.',
  },
  groupsNotSelected: {
    id: 'shell.integrations.inbound.notSelected',
    defaultMessage: 'Groups not selected',
  },
  groupsSelected: {id: 'shell.integrations.inbound.selected', defaultMessage: 'Selected groups'},
  addAll: {id: 'shell.integrations.inbound.addAll', defaultMessage: '--- ADD ALL ---'},
  removeAll: {id: 'shell.integrations.inbound.removeAll', defaultMessage: '--- DELETE ALL ---'},
  noGroups: {
    id: 'shell.integrations.inbound.noGroups',
    defaultMessage: 'No inbound groups available.',
  },
  noGroupsSelected: {
    id: 'shell.integrations.inbound.noneSelected',
    defaultMessage: 'Select at least one inbound group.',
  },
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  refreshing: {id: 'shell.integrations.inbound.refreshing', defaultMessage: 'Refreshing…'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  connecting: {id: 'shell.integrations.inbound.connecting', defaultMessage: 'Connecting…'},
  connect: {id: 'shell.integrations.inbound.connect', defaultMessage: 'Connect'},
})

const INTEGRATION_RING_ID = 'ring_init_id'
const INBOUND_CAPABLE_CAMPAIGN_ID = '0001'

const IntegrationsSectionInner = ({onClose}: {onClose?: () => void}) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const navigateAndClose = (path: string) => {
    onClose?.()
    navigate(path)
  }
  const telephony = useTelephony()

  // Google
  const [gqlCreateIntegration, {data, loading: loadingGqlGoogle, error: errorGqlGoogle}] =
    useMutation<IResCreateIntegration>(CREATE_INTEGRATION)
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false)
  const [isConnectedGoogle, setIsConnectedGoogle] = useState(
    Boolean(localStorage.getItem(STORAGE_NAMES.TOKEN_GOOGLE))
  )
  const login = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar.readonly',
    onSuccess: async (tokenResponse) => {
      const {access_token} = tokenResponse
      setIsLoadingGoogle(true)
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {Authorization: `Bearer ${access_token}`},
        })
        const resData = (await res.json()) as {email: string}
        gqlCreateIntegration({
          variables: {fromEmail: resData.email, integrationId: 1, token: access_token},
        })
        localStorage.setItem(STORAGE_NAMES.TOKEN_GOOGLE, access_token)
        localStorage.setItem(STORAGE_NAMES.EMAIL_GOOGLE, resData.email)
      } catch {
        toast.error(intl.formatMessage(M.integrationError))
      } finally {
        setIsLoadingGoogle(false)
      }
    },
    onError: (e) => toast.error(intl.formatMessage(M.errorWithMessage, {message: e.error})),
  })

  // RingCentral
  const [isConnectRing, setIsConnectRing] = useState(
    Boolean(localStorage.getItem(STORAGE_NAMES.RING_CENTRAL))
  )
  const [isOpenRingCentral, setIsOpenRingCentral] = useState(false)

  // PolicyDial
  const selectedVicidialCampaignOverrideId = telephony.vicidialSession.selectedCampaignId || ''
  const selectedVicidialCampaignId =
    selectedVicidialCampaignOverrideId || telephony.config.vicidial_campaign_id || ''
  const [showVicidialDetails, setShowVicidialDetails] = useState(false)
  const [showVicidialInboundModal, setShowVicidialInboundModal] = useState(false)
  const [connectingVicidialWithInbound, setConnectingVicidialWithInbound] = useState(false)
  const [draftVicidialCampaignId, setDraftVicidialCampaignId] = useState(
    selectedVicidialCampaignOverrideId
  )

  // Only offer active campaigns (keep the currently-selected one even if it
  // went inactive, so the dropdown still reflects the live session).
  const vicidialSelectableCampaigns = telephony.vicidialCampaignOptions.filter(
    (c) => (c.active ?? 'Y').toUpperCase() === 'Y' || c.campaign_id === selectedVicidialCampaignId
  )
  if (!vicidialSelectableCampaigns.some((c) => c.campaign_id === '0001')) {
    vicidialSelectableCampaigns.unshift({campaign_id: '0001', campaign_name: 'Manual Dial Campaign', active: 'Y'})
  }
  if (
    selectedVicidialCampaignId &&
    !vicidialSelectableCampaigns.some((c) => c.campaign_id === selectedVicidialCampaignId)
  ) {
    vicidialSelectableCampaigns.unshift({
      campaign_id: selectedVicidialCampaignId,
      campaign_name: 'Selected campaign',
      active: 'Y',
    })
  }

  const effectiveDraftVicidialCampaignId =
    draftVicidialCampaignId || telephony.config.vicidial_campaign_id || ''
  const hasVicidialCampaignChanges = draftVicidialCampaignId !== selectedVicidialCampaignOverrideId

  const activeVicidialInboundGroups = telephony.vicidialInboundGroupOptions.filter(
    (g) => (g.active ?? 'Y').toUpperCase() === 'Y'
  )
  const availableVicidialInboundGroups = activeVicidialInboundGroups.filter(
    (g) => !telephony.selectedVicidialInboundGroups.includes(g.group_id)
  )
  const selectedVicidialInboundGroupOptions = activeVicidialInboundGroups.filter((g) =>
    telephony.selectedVicidialInboundGroups.includes(g.group_id)
  )

  const vicidialStatusLabel = telephony.vicidialSession.connecting
    ? intl.formatMessage(M.registering)
    : telephony.vicidialSession.activeSession
      ? telephony.vicidialSession.status || intl.formatMessage(M.connected)
      : telephony.vicidialSession.connected
        ? intl.formatMessage(M.waiting)
        : intl.formatMessage(M.disconnected)

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!errorGqlGoogle) return
    toast.error(intl.formatMessage(M.errorWithMessage, {message: errorGqlGoogle.message}))
    setIsLoadingGoogle(false)
    localStorage.removeItem(STORAGE_NAMES.TOKEN_GOOGLE)
    localStorage.removeItem(STORAGE_NAMES.EMAIL_GOOGLE)
  }, [errorGqlGoogle])

  useEffect(() => {
    if (!data) return
    if (data.createUserIntegration?.userIntegration?.id) {
      toast.success(intl.formatMessage(M.googleConnected))
      setIsConnectedGoogle(true)
    }
  }, [data])

  useEffect(() => {
    if (!telephony.resolved || !telephony.config.vicidial_enabled || !telephony.config.vicidial_agent_user) return
    telephony.refreshVicidialSession({silent: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telephony.resolved, telephony.config.vicidial_agent_user, telephony.config.vicidial_enabled])

  useEffect(() => {
    if (!showVicidialDetails) return
    setDraftVicidialCampaignId(selectedVicidialCampaignOverrideId)
  }, [selectedVicidialCampaignOverrideId, showVicidialDetails])

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleDisconnectGoogle = () => {
    localStorage.removeItem(STORAGE_NAMES.TOKEN_GOOGLE)
    localStorage.removeItem(STORAGE_NAMES.EMAIL_GOOGLE)
    setIsConnectedGoogle(false)
    toast.info(intl.formatMessage(M.googleDisconnected))
  }

  const handleRingCentralConnect = () => {
    if (localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)) return
    /* eslint-disable */
    ;(function () {
      var rcs = document.createElement('script')
      rcs.src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?clientId=${RING_CENTRAL_CLIENT_ID}&appServer=${RING_CENTRAL_APP_SERVER}`
      rcs.id = INTEGRATION_RING_ID
      var rcs0 = document.getElementsByTagName('script')[0]
      rcs0?.parentNode?.insertBefore(rcs, rcs0)
    })()
    const script2 = document.createElement('script')
    script2.src = 'https://unpkg.com/ringcentral-c2d@1.0.0/build/index.js'
    script2.id = STORAGE_NAMES.RING_CENTRAL
    document.body.appendChild(script2)
    script2.onload = () => {
      var clickToDial = new (window as any).RingCentralC2D()
      clickToDial.on((window as any).RingCentralC2D.events.call, (phoneNumber: string) => {
        ;(window as any).RCAdapter.clickToCall(phoneNumber, true)
      })
      clickToDial.on((window as any).RingCentralC2D.events.text, (phoneNumber: string) => {
        ;(window as any).RCAdapter.clickToSMS(phoneNumber)
      })
    }
    localStorage.setItem(STORAGE_NAMES.RING_CENTRAL, STORAGE_NAMES.RING_CENTRAL)
    setIsConnectRing(true)
  }

  const handleRingCentralDisconnect = () => {
    localStorage.removeItem(STORAGE_NAMES.RING_CENTRAL)
    setIsConnectRing(false)
    window.location.reload()
  }

  const openVicidialInboundModal = async () => {
    if (
      selectedVicidialCampaignId &&
      selectedVicidialCampaignId !== INBOUND_CAPABLE_CAMPAIGN_ID
    ) {
      setConnectingVicidialWithInbound(true)
      try {
        await telephony.connectVicidialSession()
      } finally {
        setConnectingVicidialWithInbound(false)
      }
      return
    }
    await telephony.loadVicidialInboundGroups()
    setShowVicidialInboundModal(true)
  }

  const toggleVicidialInboundGroup = (groupId: string, selected: boolean) => {
    if (!groupId) return
    if (selected) {
      telephony.setVicidialInboundGroups(
        telephony.selectedVicidialInboundGroups.filter((v) => v !== groupId)
      )
    } else {
      telephony.setVicidialInboundGroups([...telephony.selectedVicidialInboundGroups, groupId])
    }
  }

  const handleVicidialConnectWithInboundGroups = async () => {
    setConnectingVicidialWithInbound(true)
    try {
      const sessionStatus = await telephony.connectVicidialSession()
      if (!sessionStatus) return
      setShowVicidialInboundModal(false)
      const ingroupsApplied = await telephony.applyVicidialInboundGroups()
      if (ingroupsApplied) await telephony.setVicidialPause(false)
    } finally {
      setConnectingVicidialWithInbound(false)
    }
  }

  const handleSaveVicidialCampaign = () => {
    telephony.setVicidialCampaign(draftVicidialCampaignId)
    if (draftVicidialCampaignId) {
      toast.success(intl.formatMessage(M.campaignSaved, {campaign: draftVicidialCampaignId}))
    } else if (effectiveDraftVicidialCampaignId) {
      toast.success(
        intl.formatMessage(M.campaignDefault, {campaign: effectiveDraftVicidialCampaignId})
      )
    } else {
      toast.success(intl.formatMessage(M.campaignCleared))
    }
    if (
      telephony.vicidialSession.activeSession &&
      telephony.vicidialSession.campaignId &&
      telephony.vicidialSession.campaignId !== effectiveDraftVicidialCampaignId
    ) {
      toast.info(intl.formatMessage(M.reconnectHint))
    }
    setShowVicidialDetails(false)
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  const isGoogleLoading = isLoadingGoogle || loadingGqlGoogle

  return (
    <>
      <div className='d-flex flex-column gap-2'>
        {/* PolicyDial Webphone */}
        <Card>
          <Card.Body className='p-3'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center gap-2'>
                <img
                  src='/media/tfi/policydial-webphone.svg'
                  alt='PolicyDial'
                  style={{height: '36px', objectFit: 'contain'}}
                />
                <span className='fw-semibold' style={{fontSize: '15px'}}>PolicyDial</span>
              </div>
              <i
                className='bi bi-arrows-fullscreen text-muted cursor-pointer'
                style={{fontSize: '13px'}}
                title={intl.formatMessage(M.openIntegrations)}
                onClick={() => navigateAndClose('/integrations')}
              />
            </div>
            <div className='d-flex align-items-center justify-content-between'>
              <Badge
                bg={
                  telephony.vicidialSession.activeSession
                    ? 'success'
                    : telephony.vicidialSession.connecting
                      ? 'warning'
                      : 'secondary'
                }
                style={{fontSize: '11px'}}
              >
                {vicidialStatusLabel}
              </Badge>
              <div className='d-flex align-items-center gap-1'>
                <Button size='sm' variant='outline-secondary' onClick={() => setShowVicidialDetails(true)}>
                  <i className='bi bi-info-circle' />
                </Button>
                {telephony.vicidialSession.connecting ||
                telephony.vicidialSession.connected ||
                telephony.vicidialSession.activeSession ? (
                  <>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      onClick={() => telephony.openWebphone('VICIDIAL', {preferAgentSession: true})}
                    >
                      <i className='bi bi-headset' />
                    </Button>
                    <Button size='sm' variant='outline-danger' onClick={telephony.disconnectVicidialSession}>
                      <i className='bi bi-power' />
                    </Button>
                  </>
                ) : (
                  <Button
                    size='sm'
                    variant='outline-success'
                    onClick={openVicidialInboundModal}
                    disabled={
                      telephony.vicidialSession.connecting ||
                      !telephony.config.vicidial_enabled ||
                      !telephony.config.vicidial_agent_user
                    }
                  >
                    {telephony.vicidialSession.connecting ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      <i className='bi bi-headset' />
                    )}
                  </Button>
                )}
                {telephony.config.vicidial_supervisor_access && (
                  <Button size='sm' variant='outline-warning' onClick={() => navigate('/telephony-supervisor')}>
                    <i className='bi bi-speedometer2' />
                  </Button>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* RingCentral */}
        <Card>
          <Card.Body className='p-3'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center gap-2'>
                <img
                  src='/media/tfi/ringcentral.png'
                  alt='RingCentral'
                  style={{height: '36px', objectFit: 'contain'}}
                />
                <span className='fw-semibold' style={{fontSize: '15px'}}>RingCentral</span>
              </div>
              <i
                className='bi bi-arrows-fullscreen text-muted cursor-pointer'
                style={{fontSize: '13px'}}
                title={intl.formatMessage(M.openIntegrations)}
                onClick={() => navigateAndClose('/integrations')}
              />
            </div>
            <div className='d-flex align-items-center justify-content-between'>
              <Badge bg={isConnectRing ? 'success' : 'secondary'} style={{fontSize: '11px'}}>
                {intl.formatMessage(isConnectRing ? M.connected : M.notConnected)}
              </Badge>
              <div className='d-flex align-items-center gap-1'>
                {isConnectRing ? (
                  <Button size='sm' variant='outline-danger' onClick={handleRingCentralDisconnect}>
                    <i className='bi bi-power' />
                  </Button>
                ) : (
                  <Button size='sm' variant='outline-secondary' onClick={handleRingCentralConnect}>
                    <i className='bi bi-plug' />
                  </Button>
                )}
                <Button size='sm' variant='outline-info' onClick={() => setIsOpenRingCentral(true)}>
                  <i className='bi bi-telephone' />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* WhatsApp */}
        <Card>
          <Card.Body className='p-3'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center gap-2'>
                <img
                  src='/media/tfi/botkmaker-1.png'
                  alt='WhatsApp'
                  style={{height: '36px', objectFit: 'contain'}}
                />
                <span className='fw-semibold' style={{fontSize: '15px'}}>WhatsApp</span>
              </div>
              <i
                className='bi bi-arrows-fullscreen text-muted cursor-pointer'
                style={{fontSize: '13px'}}
                title={intl.formatMessage(M.openTemplates)}
                onClick={() => navigateAndClose('/integrations/whatsapp-templates')}
              />
            </div>
            <div className='d-flex align-items-center justify-content-between'>
              <Badge bg='success' style={{fontSize: '11px'}}>{intl.formatMessage(M.active)}</Badge>
              <Button
                size='sm'
                variant='outline-secondary'
                onClick={() => navigateAndClose('/integrations/whatsapp-templates')}
              >
                <i className='bi bi-file-earmark-text me-1' />
                {intl.formatMessage(M.templates)}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Google */}
        {FEATURE_SHOW_GOOGLE_INTEGRATION && (
          <Card>
            <Card.Body className='p-3'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <div className='d-flex align-items-center gap-2'>
                  <img
                    src='/media/tfi/gmail.png'
                    alt='Google'
                    style={{height: '36px', objectFit: 'contain'}}
                  />
                  <span className='fw-semibold' style={{fontSize: '15px'}}>Google</span>
                </div>
                <i
                  className='bi bi-arrows-fullscreen text-muted cursor-pointer'
                  style={{fontSize: '13px'}}
                  title={intl.formatMessage(M.openIntegrations)}
                  onClick={() => navigateAndClose('/integrations')}
                />
              </div>
              <div className='d-flex align-items-center justify-content-between'>
                <Badge bg={isConnectedGoogle ? 'success' : 'secondary'} style={{fontSize: '11px'}}>
                  {intl.formatMessage(isConnectedGoogle ? M.connected : M.notConnected)}
                </Badge>
                {isConnectedGoogle ? (
                  <Button size='sm' variant='outline-danger' onClick={handleDisconnectGoogle} disabled={isGoogleLoading}>
                    <i className='bi bi-power' />
                  </Button>
                ) : (
                  <Button size='sm' variant='outline-secondary' onClick={() => login()} disabled={isGoogleLoading}>
                    {isGoogleLoading ? <Spinner animation='border' size='sm' /> : <i className='bi bi-plug' />}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* RingCentral webphone modal */}
      {isOpenRingCentral && (
        <ModalRingCentralConnect isOpen={isOpenRingCentral} onClose={() => setIsOpenRingCentral(false)} />
      )}

      {/* PolicyDial Details Modal */}
      <Modal show={showVicidialDetails} onHide={() => setShowVicidialDetails(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage(M.detailsTitle)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='border rounded p-3 mb-3 bg-light'>
            <div className='fw-semibold text-dark mb-2'>{intl.formatMessage(M.sessionCampaign)}</div>
            <div className='d-flex align-items-end gap-2 flex-wrap'>
              <Form.Group className='flex-grow-1 mb-0' style={{minWidth: '220px'}}>
                <Form.Select
                  size='sm'
                  value={draftVicidialCampaignId}
                  onChange={(e) => setDraftVicidialCampaignId(e.target.value)}
                >
                  <option value=''>
                    {intl.formatMessage(M.useOrgDefault)}
                    {telephony.config.vicidial_campaign_id
                      ? ` (${telephony.config.vicidial_campaign_id})`
                      : ''}
                  </option>
                  {vicidialSelectableCampaigns.map((campaign) => (
                    <option key={campaign.campaign_id} value={campaign.campaign_id}>
                      {campaign.campaign_id} - {campaign.campaign_name}
                      {campaign.active === 'Y' ? '' : ' (Inactive)'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant='light-primary'
                size='sm'
                onClick={() => telephony.loadVicidialCampaigns()}
                disabled={telephony.loadingVicidialCampaigns}
              >
                {intl.formatMessage(telephony.loadingVicidialCampaigns ? M.loadingCampaigns : M.loadCampaigns)}
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={handleSaveVicidialCampaign}
                disabled={!hasVicidialCampaignChanges}
              >
                <i className='bi bi-check-lg me-1' />
                Save
              </Button>
            </div>
            <div className='mt-2'>
              Keep <code>0001</code> available when you need manual dialing.
            </div>
          </div>
          <div className='text-muted fs-7'>
            <div>
              <strong>{intl.formatMessage(M.fieldAgent)}</strong> {telephony.vicidialSession.agentUser || intl.formatMessage(M.notConfigured)}
            </div>
            <div>
              <strong>{intl.formatMessage(M.fieldPhone)}</strong> {telephony.vicidialSession.phoneLogin || intl.formatMessage(M.notConfigured)}
            </div>
            <div>
              <strong>{intl.formatMessage(M.fieldStatus)}</strong> {vicidialStatusLabel}
            </div>
            {telephony.vicidialSession.campaignId &&
            telephony.vicidialSession.campaignId !== effectiveDraftVicidialCampaignId ? (
              <div>
                <strong>{intl.formatMessage(M.fieldActiveCampaign)}</strong> {telephony.vicidialSession.campaignId}
              </div>
            ) : null}
            {telephony.vicidialSession.pauseCode && (
              <div>
                <strong>{intl.formatMessage(M.fieldPauseCode)}</strong> {telephony.vicidialSession.pauseCode}
              </div>
            )}
            {telephony.vicidialSession.sessionId && (
              <div>
                <strong>{intl.formatMessage(M.fieldSession)}</strong> {telephony.vicidialSession.sessionId}
              </div>
            )}
            <div className='mt-2'>{telephony.vicidialSession.message}</div>
          </div>
        </Modal.Body>
      </Modal>

      {/* PolicyDial Inbound Group Selection Modal */}
      <Modal
        show={showVicidialInboundModal}
        onHide={() => !connectingVicidialWithInbound && setShowVicidialInboundModal(false)}
        centered
        size='lg'
      >
        <Modal.Header closeButton={!connectingVicidialWithInbound}>
          <Modal.Title>{intl.formatMessage(M.inboundTitle)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='text-muted fs-7 mb-3'>
            {intl.formatMessage(M.inboundHint)}
          </div>
          <div className='row g-3'>
            <div className='col-md-6'>
              <div className='fw-bold text-uppercase text-center mb-2'>{intl.formatMessage(M.groupsNotSelected)}</div>
              <div
                className='border rounded p-2 bg-light'
                style={{minHeight: '280px', maxHeight: '320px', overflowY: 'auto'}}
              >
                <div
                  role='button'
                  className='text-center fw-semibold text-primary mb-2'
                  onClick={() =>
                    telephony.setVicidialInboundGroups(
                      activeVicidialInboundGroups.map((g) => g.group_id)
                    )
                  }
                >
                  {intl.formatMessage(M.addAll)}
                </div>
                {availableVicidialInboundGroups.length ? (
                  availableVicidialInboundGroups.map((group) => (
                    <div
                      key={group.group_id}
                      role='button'
                      className='py-1 text-primary text-decoration-underline'
                      onClick={() => toggleVicidialInboundGroup(group.group_id, false)}
                    >
                      {group.group_name}
                      {group.group_name !== group.group_id && (
                        <span className='text-muted small ms-2'>({group.group_id})</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className='text-muted small'>{intl.formatMessage(M.noGroups)}</div>
                )}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='fw-bold text-uppercase text-center mb-2'>{intl.formatMessage(M.groupsSelected)}</div>
              <div
                className='border rounded p-2 bg-light'
                style={{minHeight: '280px', maxHeight: '320px', overflowY: 'auto'}}
              >
                <div
                  role='button'
                  className='text-center fw-semibold text-primary mb-2'
                  onClick={() => telephony.setVicidialInboundGroups([])}
                >
                  {intl.formatMessage(M.removeAll)}
                </div>
                {selectedVicidialInboundGroupOptions.length ? (
                  selectedVicidialInboundGroupOptions.map((group) => (
                    <div
                      key={group.group_id}
                      role='button'
                      className='py-1 text-primary text-decoration-underline'
                      onClick={() => toggleVicidialInboundGroup(group.group_id, true)}
                    >
                      {group.group_name}
                      {group.group_name !== group.group_id && (
                        <span className='text-muted small ms-2'>({group.group_id})</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className='text-muted small'>{intl.formatMessage(M.noGroupsSelected)}</div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => setShowVicidialInboundModal(false)}
            disabled={connectingVicidialWithInbound}
          >
            {intl.formatMessage(M.cancel)}
          </Button>
          <Button
            variant='light-primary'
            onClick={() => telephony.loadVicidialInboundGroups()}
            disabled={telephony.loadingVicidialInboundGroups || connectingVicidialWithInbound}
          >
            {intl.formatMessage(telephony.loadingVicidialInboundGroups ? M.refreshing : M.refresh)}
          </Button>
          <Button
            variant='primary'
            onClick={handleVicidialConnectWithInboundGroups}
            disabled={connectingVicidialWithInbound || !telephony.selectedVicidialInboundGroups.length}
          >
            {intl.formatMessage(connectingVicidialWithInbound ? M.connecting : M.connect)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export const NavbarIntegrationsSection = ({onClose}: {onClose?: () => void}) => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <IntegrationsSectionInner onClose={onClose} />
    </GoogleOAuthProvider>
  )
}
